import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    ButtonGroup,
    Chip,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Menu,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { createCms, getCms, deleteCms, updateCms } from '../Apis/CategoriesApis';

// Helper to strip HTML tags for table preview
const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>?/gm, ' ').replace(/\s+/g, ' ').trim();
};

const Cms = () => {
    const { categoryId, optionId } = useParams();
    const [cmsContent, setCmsContent] = useState([]);
    const [open, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newContent, setNewContent] = useState({
        plan: null,
        description: '',
        PriceText: ''
    });
    const [planFilter, setPlanFilter] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedContent, setSelectedContent] = useState(null);

    const fetchCmsContent = async () => {
        setIsLoading(true);
        try {
            const response = await getCms(categoryId, optionId || "null", planFilter || undefined);
            if (response.data && response.data.status) {
                setCmsContent(response.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching CMS content:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCmsContent();
    }, [categoryId, optionId, planFilter]);

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setNewContent({ plan: null, description: '', PriceText: '' });
        setEditingId(null);
    };

    const handleMenuOpen = (event, content) => {
        setAnchorEl(event.currentTarget);
        setSelectedContent(content);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedContent(null);
    };

    const handleEdit = (content) => {
        setEditingId(content.id);
        setNewContent({
            plan: content.plan || null,
            description: content.description || '',
            PriceText: content.PriceText || ''
        });

        handleMenuClose();
        setOpen(true);
    };

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setNewContent(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const onEditorChange = (content) => {
        setNewContent(prev => ({ ...prev, description: content }));
    };


    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            const htmlContent = newContent.description;

            const formData = new FormData();
            formData.append('title', ''); // Title is removed but kept empty for API
            formData.append('description', htmlContent);
            if (newContent.plan) {
                formData.append('plan', newContent.plan);
            } else {
                formData.append('plan', '');
            }
            formData.append('PriceText', newContent.PriceText || '');
            if (categoryId) formData.append('categoryId', categoryId);
            if (optionId) formData.append('optionId', optionId);

            let response;
            if (editingId) {
                response = await updateCms(editingId, formData);
            } else {
                response = await createCms(formData);
            }

            if (response.data && response.data.status) {
                setOpen(false);
                setNewContent({ plan: null, description: '', PriceText: '' });
                setEditingId(null);
                fetchCmsContent(); // Refresh list
            } else {
                alert(response.data.message || "Failed to save CMS content");
            }
        } catch (error) {
            console.error("Error saving CMS content:", error);
            alert(error.response?.data?.message || "Something went wrong! Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (content) => {
        if (window.confirm('Are you sure you want to delete this content?')) {
            try {
                await deleteCms(content.id);
                fetchCmsContent(); // Refresh list
            } catch (error) {
                console.error("Error deleting CMS content:", error);
            }
        }
        handleMenuClose();
    };

    return (
        <Container maxWidth="xl" >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" component="h1">
                    CMS Content
                </Typography>
                <Box display="flex" gap={2} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Filter by Plan</InputLabel>
                        <Select
                            value={planFilter}
                            label="Filter by Plan"
                            onChange={(e) => setPlanFilter(e.target.value)}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="Basic">Basic</MenuItem>
                            <MenuItem value="Premium">Premium</MenuItem>
                            <MenuItem value="Luxury">Luxury</MenuItem>
                        </Select>
                    </FormControl>
                    <Button variant="contained" color="primary" onClick={handleOpen}>
                        Add Content
                    </Button>
                </Box>
            </Box>

            {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Category</TableCell>
                                <TableCell>Option</TableCell>
                                <TableCell>Plan</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {cmsContent.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        No content found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                cmsContent.map((content, index) => (
                                    <TableRow key={content.id}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>
                                            <Box
                                                sx={{
                                                    maxWidth: '300px',
                                                    maxHeight: '150px', // Increased height to show more content
                                                    overflowY: 'auto',   // Scroll if very long
                                                    padding: '4px',
                                                    '& *': {
                                                        fontSize: '13px !important',
                                                        margin: '0 !important',
                                                        lineHeight: '1.4 !important',
                                                    },
                                                    '& ul, & ol': {
                                                        paddingLeft: '20px !important'
                                                    }
                                                }}
                                                dangerouslySetInnerHTML={{ __html: content.description }}
                                            />
                                        </TableCell>
                                        <TableCell>{content?.Category?.name || '-'}</TableCell>
                                        <TableCell>{content?.CategoryOption?.optionName || '-'}</TableCell>
                                        <TableCell>{content?.plan || '-'}</TableCell>
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleMenuOpen(e, content)}
                                            >
                                                <MoreVertIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => selectedContent && handleEdit(selectedContent)}>
                    <ListItemIcon>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Edit</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => selectedContent && handleDelete(selectedContent)}>
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText>Delete</ListItemText>
                </MenuItem>
            </Menu>

            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>{editingId ? 'Edit CMS Content' : 'Add CMS Content'}</DialogTitle>
                <DialogContent>
                    <TextField
                        margin="dense"
                        name="plan"
                        label="Plan Type"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={newContent.plan || ''}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        name="PriceText"
                        label="Price Text (e.g. ['Best Price', 'Limited Offer'])"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={newContent.PriceText || ''}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                    />
                    <Typography variant="body2" sx={{ mb: 1, mt: 2 }}>
                        Description
                    </Typography>
                    <Box sx={{
                        '& .ql-container': {
                            minHeight: '250px',
                            fontSize: '14px'
                        },
                        '& .ql-editor': {
                            minHeight: '250px'
                        }
                    }}>
                        <ReactQuill
                            theme="snow"
                            value={newContent.description}
                            onChange={onEditorChange}
                            modules={{
                                toolbar: [
                                    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                                    ['bold', 'italic', 'underline', 'strike'],
                                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                    [{ 'align': [] }],
                                    ['link', 'clean']
                                ],
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ mt: 2 }}>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isSubmitting}>
                        {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};


export default Cms;